import { AutoCustomCompleteField, CustomDateControl, CustomTimefield, FormField, FormGrid, FormShell, Input, MessageAlert, RBButton, RbToolbarActionButton } from '@scmlatam/web-ui';
import { Icon } from '@mui/material';
import type { useFormAddShift } from '../hooks/useFormAddShift';
import type { ShiftSegmentType } from '../types/services';

interface AddShiftDialogProps {
    formProps: ReturnType<typeof useFormAddShift>;
}

const AddShiftDialog = ({ formProps }: AddShiftDialogProps) => {
    return (
        <FormShell
            open={formProps.openForm}
            title={formProps.title}
            size="m"
            onClose={formProps.closeForm}
            loading={formProps.loading}
            alert={
                formProps.validationMessage ? (
                    <div id="schedule-add-shift-alert-validation">
                        <MessageAlert
                            type="warning"
                            title={formProps.validationMessage}
                            message=""
                            actions={[
                                {
                                    label: 'OK',
                                    onClick: () => formProps.setValidationMessage(''),
                                },
                            ]}
                        />
                    </div>
                ) : undefined
            }
            footer={
                <div id="schedule-add-shift-footer" className="flex flex-wrap items-center justify-end gap-3 p-3">
                    <RBButton id="schedule-add-shift-cancel" variant="secondary" size="m" disabled={formProps.loading} onClick={formProps.closeForm}>
                        {formProps.t('add_shift.buttons.cancel')}
                    </RBButton>
                    <RBButton id="schedule-add-shift-save" size="m" loading={formProps.loading} disabled={!formProps.canSubmit} onClick={formProps.submit}>
                        {formProps.t('add_shift.buttons.add')}
                    </RBButton>
                </div>
            }
        >
            <FormGrid id="schedule-add-shift-form">
                <FormField span={12}>
                    <AutoCustomCompleteField
                        id="schedule-add-shift-workers"
                        label={formProps.t('add_shift.fields.workers')}
                        placeholder={formProps.t('add_shift.placeholder.select')}
                        values={formProps.employees}
                        value={formProps.employeeIds}
                        setValue={formProps.setEmployeeIds}
                        white
                        search={{
                            value: formProps.searchEmployee,
                            onChange: formProps.setSearchEmployee,
                            placeholder: formProps.t('add_shift.placeholder.select'),
                            mode: 'local',
                            emptyText: formProps.t('add_shift.placeholder.select'),
                        }}
                    />
                </FormField>

                <FormField id="schedule-add-shift-date" span={6}>
                    <CustomDateControl label={formProps.t('add_shift.fields.date')} initialValue={null} value={formProps.date} setValue={formProps.setDate} disabled={false} white fullWidth />
                </FormField>

                <FormField span={6}>
                    <Input
                        id="schedule-add-shift-repeat"
                        type="number"
                        label={formProps.t('add_shift.fields.repeat')}
                        labelVariant="notched"
                        value={formProps.repeat}
                        onChange={(event) => formProps.setRepeat(Number(event.target.value))}
                        min={1}
                    />
                </FormField>

                <FormField span={12}>
                    <div className="overflow-hidden rounded-lg border" style={{ borderColor: '#CECDCA' }}>
                        <table id="schedule-add-shift-segments" className="w-full border-collapse">
                            <thead className="border-b" style={{ borderColor: '#CECDCA' }}>
                                <tr>
                                    <th className="w-12" />
                                    <th className="p-3 text-left font-semibold">{formProps.t('add_shift.fields.start_time')}</th>
                                    <th className="p-3 text-left font-semibold">{formProps.t('add_shift.fields.end_time')}</th>
                                    <th className="p-3 text-left font-semibold">{formProps.t('add_shift.fields.type')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formProps.segments.map((segment) => (
                                    <tr id={`schedule-add-shift-segment-row-${segment.id}`} key={segment.id}>
                                        <td className="p-2 text-center">
                                            <RbToolbarActionButton
                                                id={`schedule-add-shift-segment-remove-${segment.id}`}
                                                aria-label="Remove segment"
                                                variant="icon"
                                                disabled={formProps.segments.length === 1}
                                                onClick={() => formProps.removeSegment(segment.id)}
                                                icon={
                                                    <Icon baseClassName="material-symbols-rounded" sx={{ color: '#190007', fontSize: '20px' }}>
                                                        remove_circle
                                                    </Icon>
                                                }
                                            />
                                        </td>
                                        <td id={`schedule-add-shift-segment-start-${segment.id}`} className="p-2">
                                            <CustomTimefield
                                                label=""
                                                initialValue={null}
                                                value={segment.start}
                                                setValue={(start) => formProps.updateSegment(segment.id, { start })}
                                                disabled={false}
                                                white
                                                fullWidth
                                                error={formProps.segmentValidation.invalidSegmentIds.has(segment.id)}
                                                helperText={formProps.segmentValidation.invalidSegmentIds.has(segment.id) ? formProps.t('add_shift.alerts.validation.segment_overlap') : ''}
                                            />
                                        </td>
                                        <td id={`schedule-add-shift-segment-end-${segment.id}`} className="p-2">
                                            <CustomTimefield
                                                label=""
                                                initialValue={null}
                                                value={segment.end}
                                                setValue={(end) => formProps.updateSegment(segment.id, { end })}
                                                disabled={false}
                                                white
                                                fullWidth
                                                error={formProps.segmentValidation.invalidSegmentIds.has(segment.id)}
                                            />
                                        </td>
                                        <td className="p-2">
                                            <AutoCustomCompleteField
                                                id={`schedule-add-shift-segment-type-${segment.id}`}
                                                label=""
                                                value={segment.type}
                                                setValue={(type) =>
                                                    formProps.updateSegment(segment.id, {
                                                        type: type as ShiftSegmentType,
                                                    })
                                                }
                                                values={[
                                                    {
                                                        label: formProps.t('add_shift.fields.regular'),
                                                        value: 'REGULAR',
                                                    },
                                                    {
                                                        label: formProps.t('add_shift.fields.break'),
                                                        value: 'BREAK',
                                                    },
                                                ]}
                                                placeholder={formProps.t('add_shift.placeholder.select')}
                                                disabled={false}
                                                selectionMode="single"
                                                white
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="p-2">
                            <RBButton
                                id="schedule-add-shift-add-segment"
                                variant="secondary"
                                size="s"
                                rightIcon={<Icon baseClassName="material-symbols-rounded">add</Icon>}
                                onClick={formProps.addSegment}
                            >
                                {formProps.t('add_shift.buttons.add_row')}
                            </RBButton>
                        </div>
                    </div>
                </FormField>
            </FormGrid>
        </FormShell>
    );
};

export { AddShiftDialog };
